/**
 * Placa veicular compacta inspirada no padrão Mercosul.
 * Reutilizada em cartões e modais para manter a identificação consistente.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-26 v1.1.0 - Centraliza a interação para abrir o veículo,
 *          com indicação visual no mouse/foco e suporte nativo ao teclado.
 * RELEASE: 2026-09-26 v1.0.0 - Extração do padrão visual compartilhado.
 */
import { Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface VehiclePlateChipProps {
  plate?: string | null;
  compact?: boolean;
  className?: string;
  /** Quando informado, torna a placa acessível e sinaliza a abertura do veículo. */
  onOpenVehicle?: () => void;
}

export function VehiclePlateChip({ plate, compact = false, className, onOpenVehicle }: VehiclePlateChipProps) {
  const plateContent = (
    <span
      className={cn(
        'inline-flex shrink-0 flex-col overflow-hidden rounded border-2 border-foreground bg-card shadow-sm',
        compact && 'rounded-[3px] border',
        !onOpenVehicle && className,
      )}
      title={plate || 'Sem placa'}
    >
      <span className={cn('h-1.5 w-full bg-primary', compact && 'h-1')} />
      <span className={cn(
        'px-2 py-0.5 text-center font-mono text-xs font-bold tracking-widest text-foreground leading-tight',
        compact && 'px-1.5 py-px text-[9px]',
      )}>
        {plate ?? '—'}
      </span>
    </span>
  );

  if (!onOpenVehicle) return plateContent;

  return (
    <Button
      type="button"
      variant="ghost"
      className={cn(
        'group/vehicle h-auto shrink-0 gap-0.5 rounded-sm p-0 transition-colors hover:bg-transparent hover:text-primary focus-visible:ring-1',
        className,
      )}
      title="Abrir veículo em uma janela separada"
      onClick={(event) => {
        event.stopPropagation();
        onOpenVehicle();
      }}
    >
      {plateContent}
      <Car className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/vehicle:opacity-100 group-focus-visible/vehicle:opacity-100" />
    </Button>
  );
}